/**
 * ScansV2Page – Main container for the new Scans page.
 *
 * Features:
 * - Toggle between Scans and Schedules views via IOSSwitch
 * - "New Scan" dropdown button (Discovery, On-Prem, AWS)
 * - "+" button visible only in Schedules view
 * - Uses AgGridTable with API data
 */

import { useState, useMemo, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  FormControlLabel,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Dialog,
  DialogContent,
  Stack,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import AddIcon from "@mui/icons-material/Add";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
// import moment from "moment-timezone";

import AgGridTable from "../../../components/table/AgGridTable";
import IOSSwitch from "../../../components/IOSSwitch";
import { useScansColumnDefs } from "./ScansTableView";
import { useSchedulesColumnDefs } from "./SchedulesTableView";
import CreateScheduleDrawer from "./CreateScheduleDrawer";
import type { ScanRow, ScanStatus } from "./scansV2.types";
import type { ScheduleRow } from "./scansV2.types";
import { parseFlexibleDate } from "./scansV2.utils";
import RenameModel from "../myScan/RenameModel";
import ChangeScheduleModal from "../myScan/ChangeScheduleModal";
import { myScanActions } from "../../../store/onPremMyscanSlice";
import { setEdit } from "../myScan/MyScanTable_Details_slice";
import useRolePermissions from "../../../shared/hooks/useRolePermissions";
import { ROLES_CONTENT_PROPS } from "../../../shared/constants/constant-data";
import { RootState } from "../../../store/store";
import axiosInstance from "../../../axios/axios";
import { END_POINTS } from "../../../shared";
import NewScanDrawer, { type ScanTypeId } from "../newScanV2/NewScanDrawer";
import DiscoveryRescanPopup from "../../discovery/DiscoveryRescanPopup";
import {
  setIsNewUserTrue,
  setIsOnlyPremAssetUser,
  setIsOnlyCloudUser,
  setIsBothCloudAndPremAssetUser,
  resetUserTypeFlags,
} from "../../../modules/authentication/login/Login_Details_slice";

// ──────────────────────────────────────────────
// New Scan Dropdown Component
// ──────────────────────────────────────────────

interface NewScanDropdownProps {
  onScanTypeSelect: (scanType: ScanTypeId) => void;
}

const NewScanDropdown: React.FC<NewScanDropdownProps> = ({ onScanTypeSelect }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleOptionClick = (scanType: ScanTypeId) => {
    onScanTypeSelect(scanType);
    handleClose();
  };

  return (
    <>
      <Button
        variant="contained"
        onClick={handleClick}
        endIcon={<KeyboardArrowDownIcon />}
        sx={{
          backgroundColor: "#3A2ADA",
          borderRadius: "8px",
          textTransform: "none",
          fontWeight: 600,
          fontSize: "13px",
          px: 2,
          py: 0.8,
          "&:hover": {
            backgroundColor: "#2E1FBF",
          },
        }}
      >
        New Scan
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.5,
              borderRadius: "8px",
              boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.12)",
              minWidth: 180,
            },
          },
        }}
      >
        <MenuItem
          onClick={() => handleOptionClick("onprem-discovery")}
          sx={{ fontSize: "14px", py: 1 }}
        >
          On-Prem Discovery
        </MenuItem>
        <MenuItem
          onClick={() => handleOptionClick("onprem-discovery-posture")}
          sx={{ fontSize: "14px", py: 1 }}
        >
          On-Prem Discovery + Posture
        </MenuItem>
        <MenuItem
          onClick={() => handleOptionClick("onprem-posture")}
          sx={{ fontSize: "14px", py: 1 }}
        >
          On-Prem Posture
        </MenuItem>
      </Menu>
    </>
  );
};

// ──────────────────────────────────────────────
// Main Page Component
// ──────────────────────────────────────────────

const ScansV2Page = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { hasPermissions } = useRolePermissions();
  const renameDetails = useSelector((state: RootState) => state.onPremMyScan);
  const myscanSocketData = useSelector(
    (state: RootState) => state.MyScanDetails.myscanSocketData
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const isScheduleView = searchParams.get("view") === "schedule";
  const [isChecked, setIsChecked] = useState(isScheduleView);
  const [isCreateScheduleOpen, setIsCreateScheduleOpen] = useState(false);

  // New Scan Drawer state
  const [isNewScanDrawerOpen, setIsNewScanDrawerOpen] = useState(false);
  const [selectedScanType, setSelectedScanType] = useState<ScanTypeId>("onprem-discovery");

  // Rescan popup state
  const [isRescanPopupOpen, setIsRescanPopupOpen] = useState(false);
  const [rescanRowData, setRescanRowData] = useState<ScanRow | null>(null);

  // Pause/Resume state
  const [isPauseResumeDialogOpen, setIsPauseResumeDialogOpen] = useState(false);
  const [pauseResumeType, setPauseResumeType] = useState<"pause" | "resume">("pause");
  const [selectedSchedule, setSelectedSchedule] = useState<ScheduleRow | null>(null);

  // Scans data state
  const [scansData, setScansData] = useState<ScanRow[]>([]);
  const [isLoadingScans, setIsLoadingScans] = useState(false);
  const [schedulesData, setSchedulesData] = useState<ScheduleRow[]>([]);
  const [isLoadingSchedules, setIsLoadingSchedules] = useState(false);
  const [agentStatuses, setAgentStatuses] = useState<Record<string, string>>({});
  const [cloudAgents, setCloudAgents] = useState<any[]>([]);

  useEffect(() => {
    const fetchAgents = async () => {
      try {
        const res = await axiosInstance.get(END_POINTS.CLOUD_AGENT);
        if (res.data?.data) {
          const agents = res.data.data;
          setCloudAgents(agents);
          const statuses: Record<string, string> = {};
          agents.forEach((a: any) => {
            if (a.id) statuses[a.id] = String(a.status_display ?? a.status ?? "").toLowerCase();
          });
          setAgentStatuses(statuses);
        }
      } catch (err) {
        console.error("Failed to fetch agents", err);
      }
    };
    fetchAgents();
  }, []);

  const mapScanStatus = (status: unknown, scanType: unknown): ScanStatus => {
    // Coerce numeric-looking strings ("3", "4"...) to real numbers up front
    const numericStatus =
      typeof status === "string" && status.trim() !== "" && !isNaN(Number(status))
        ? Number(status)
        : status;

    if (numericStatus === 1 || numericStatus === 2) {
      return "In Progress";
    }

    const statusByCode: Record<number, ScanStatus> = {
      3: "Success",
      4: "Failed",
      5: "Cancelled",
      6: "Partially Success",
      7: "Fetching Configuration",
      8: "Fetching Configuration Success",
      9: "Fetching Configuration Failed",
    };

    if (typeof numericStatus === "number" && statusByCode[numericStatus]) {
      const mapped = statusByCode[numericStatus];
      console.log(`🟩 [mapScanStatus] raw status:`, status, `-> numericStatus:`, numericStatus, `-> mapped:`, mapped);
      return mapped;
    }

    if (typeof status === "string" && status.trim() && isNaN(Number(status))) {
      const normalizedStatus = status.trim().toLowerCase();
      const statusByLabel: Record<string, ScanStatus> = {
        queue: "Queue",
        processing: "Processing",
        completed: "Completed",
        success: "Success",
        failed: "Failed",
        cancelled: "Cancelled",
        "partially success": "Partially Success",
        "in progress": "In Progress",
        "fetching configuration": "Fetching Configuration",
        "fetching configuration success": "Fetching Configuration Success",
        "fetching configuration failed": "Fetching Configuration Failed",
        paused: "Paused",
        pause: "Paused",
      };

      if (statusByLabel[normalizedStatus]) {
        const mapped = statusByLabel[normalizedStatus];
        console.log(`🟩 [mapScanStatus] raw status:`, status, `-> normalizedStatus:`, normalizedStatus, `-> mapped:`, mapped);
        return mapped;
      }
    }

    console.log(`🟩 [mapScanStatus] raw status:`, status, `-> defaulting to: In Progress`);
    return "In Progress";
  };

  // Helper to update asset count and user type flags
  const updateAssetCount = async () => {
    try {
      dispatch(resetUserTypeFlags());
      const res = await axiosInstance.get(END_POINTS.IS_NEW_USER);
      if (res.status === 200) {
        dispatch(setIsOnlyPremAssetUser({ isOnlyPremAssetUser: false }));
        dispatch(setIsOnlyCloudUser({ isOnlyCloudUser: false }));
        dispatch(
          setIsBothCloudAndPremAssetUser({
            isBothCloudAndPremAssetUser: false,
          })
        );
        const parsed = res?.data?.data || false;
        if (parsed.device_count === 0 && parsed.cloud_count === 0) {
          dispatch(setIsNewUserTrue({ isNewUserTrue: true }));
        } else if (parsed.device_count > 0 && parsed.cloud_count === 0) {
          dispatch(setIsOnlyPremAssetUser({ isOnlyPremAssetUser: true }));
        } else if (parsed.device_count === 0 && parsed.cloud_count > 0) {
          dispatch(setIsOnlyCloudUser({ isOnlyCloudUser: true }));
        } else if (parsed.device_count > 0 && parsed.cloud_count > 0) {
          dispatch(
            setIsBothCloudAndPremAssetUser({
              isBothCloudAndPremAssetUser: true,
            })
          );
        }
      }
    } catch (error) {
      console.error("Error updating asset count:", error);
    }
  };

  // Update scans data with real-time socket events
  useEffect(() => {
    if (!myscanSocketData || myscanSocketData.length === 0) return;

    setScansData((prevData: ScanRow[]) => {
      const updatedData = [...prevData];

      myscanSocketData.forEach((incomingRow: any) => {
        const rawType = incomingRow.type || incomingRow.scan_type || "";
        const isDiscoveryType = String(rawType).toLowerCase().includes("discovery");
        const rawRescan = incomingRow.reScan ?? incomingRow.rescan;
        const isRescanSpecified = rawRescan !== undefined && rawRescan !== null;
        const isRescanEnabled = isRescanSpecified
          ? (rawRescan === true ||
             rawRescan === 1 ||
             rawRescan === "1" ||
             String(rawRescan ?? "").toLowerCase() === "true")
          : undefined;

        const rawDateTime = incomingRow.date_time || incomingRow.scan_time || incomingRow.scanTime || null;
        const convertedScanTime = rawDateTime || undefined;

        let mappedSchedule: any = undefined;
        const incomingScheduleObj = incomingRow.schedule !== undefined ? incomingRow.schedule : incomingRow.schedule_obj;
        if (incomingScheduleObj !== undefined) {
          if (incomingScheduleObj === null) {
            mappedSchedule = null;
          } else {
            mappedSchedule = { ...incomingScheduleObj };
          }
        }

        const mappedRow: Partial<ScanRow> = {
          id: incomingRow.id || incomingRow.scan_uuid || incomingRow.scan_id,
          name: incomingRow.scan_name || incomingRow.scanname || incomingRow.name,
          scanName: incomingRow.scan_name || incomingRow.scanname,
          scan_uuid: incomingRow.scan_uuid || incomingRow.scan_id,
          scanId: incomingRow.scan_id || incomingRow.id || incomingRow.scan_uuid,
          scheduleId: incomingRow.schedule_id ?? incomingScheduleObj?.schedule_id ?? incomingScheduleObj?.id,
          schedule_id: incomingRow.schedule_id ?? incomingScheduleObj?.schedule_id ?? incomingScheduleObj?.id,
          schedule_status: incomingRow.schedule_status ?? incomingScheduleObj?.status,
          statusTemp: incomingRow.status,
          reScan: isRescanEnabled,
          schedule: mappedSchedule,
          type: rawType,
          scanTime: convertedScanTime,
          status: mapScanStatus(incomingRow.status, rawType),
          assetsDiscovered: rawType === "onprem_posture" 
            ? (incomingRow.discovered_assets ?? incomingRow.assetsDiscovered ?? 0)
            : rawType === "aws_discovery_posture"
            ? (incomingRow.discovered_assets ?? incomingRow.assetsDiscovered ?? null)
            : (incomingRow.discovered_assets ?? incomingRow.assetsDiscovered ?? incomingRow.numberofdevices),
          assetsAssessed: incomingRow.assessed_assets ?? incomingRow.assessedAssets ?? incomingRow.passed_count,
          passedAssets: incomingRow.passed_count,
          TotalAssets: incomingRow.total_count,
          misconfigurations: incomingRow.misconfigurations ?? incomingRow.vulnerabilities,
          agent: incomingRow.agent_name || incomingRow.agent,
          agent_id: incomingRow.agent_id || incomingRow.agentId,
          user: incomingRow.user || incomingRow.created_by || incomingRow.user_id,
          onprem_posture_id: incomingRow.onprem_posture_id,
          host: incomingRow.host || incomingRow.ip || incomingRow.ip_address || incomingRow.ipAddress,
          ip_range: incomingRow.ip_range || incomingRow.ipRange,
          cred_ids: incomingRow.cred_ids || (incomingRow.cred_id ? [incomingRow.cred_id] : undefined),
        };

        // Filter out undefined properties so they don't overwrite valid existing properties on merge
        const cleanMappedRow = Object.fromEntries(
          Object.entries(mappedRow).filter(([, v]) => v !== undefined)
        ) as Partial<ScanRow>;

        const incomingTargetId =
          incomingRow.scan_uuid ||
          incomingRow.scan_id ||
          incomingRow.id ||
          incomingRow.onprem_posture_id ||
          incomingRow.onprem_id ||
          incomingRow.file_id;

        const existingIndex = updatedData.findIndex((row: ScanRow) => {
          if (
            incomingTargetId &&
            (row.scan_uuid === incomingTargetId ||
              row.scanId === incomingTargetId ||
              row.id === incomingTargetId ||
              row.onprem_posture_id === incomingTargetId)
          ) {
            return true;
          }
          return false;
        });

        // Only fall back to name-matching if no id-based match was found,
        // AND the name is unique among rows still in progress (safer than matching any historical row)
        let finalIndex = existingIndex;
        if (finalIndex === -1) {
          const incomingName = incomingRow.scan_name || incomingRow.scanname || incomingRow.name;
          if (incomingName) {
            const candidates = updatedData
              .map((row, idx) => ({ row, idx }))
              .filter(({ row }) => {
                const nameMatches = row.scanName === incomingName || row.name === incomingName;
                const stillInProgress =
                  String(row.status ?? "").toLowerCase() === "in progress" ||
                  row.statusTemp === 1 ||
                  row.statusTemp === 2 ||
                  row.statusTemp === "1" ||
                  row.statusTemp === "2";
                return nameMatches && stillInProgress;
              });
            // Only trust the fallback if it's unambiguous
            if (candidates.length === 1) {
              finalIndex = candidates[0].idx;
            }
          }
        }

        console.log(`🟨 [scansData socket] incomingTargetId:`, incomingTargetId, `finalIndex:`, finalIndex, `incomingRow:`, incomingRow);

        if (finalIndex !== -1) {
          const existingRow = updatedData[finalIndex];
          const finalType = rawType || existingRow.type || "";
          const finalIsDiscovery = String(finalType).toLowerCase().includes("discovery");
          const finalStatus = mapScanStatus(incomingRow.status, finalType);
          const finalAssetsAssessed = incomingRow.assessed_assets ?? incomingRow.assessedAssets ?? incomingRow.passed_count ?? existingRow.assetsAssessed ?? existingRow.passedAssets ?? 0;

          updatedData[finalIndex] = {
            ...existingRow,
            ...cleanMappedRow,
            type: finalType,
            status: finalStatus,
            assetsAssessed: finalAssetsAssessed,
            assetsDiscovered: finalType === "onprem_posture" 
              ? (incomingRow.discovered_assets ?? incomingRow.assetsDiscovered ?? existingRow.assetsDiscovered ?? 0) 
              : finalType === "aws_discovery_posture"
              ? (incomingRow.discovered_assets ?? incomingRow.assetsDiscovered ?? existingRow.assetsDiscovered ?? null)
              : (incomingRow.discovered_assets ?? incomingRow.assetsDiscovered ?? incomingRow.numberofdevices ?? existingRow.assetsDiscovered),
            misconfigurations: incomingRow.misconfigurations ?? incomingRow.vulnerabilities ?? existingRow.misconfigurations,
            agent: incomingRow.agent_name || incomingRow.agent || existingRow.agent || "N/A",
            user: incomingRow.user || incomingRow.created_by || incomingRow.user_id || existingRow.user || "N/A",
            onprem_posture_id: incomingRow.onprem_posture_id || existingRow.onprem_posture_id,
            schedule_status: incomingRow.schedule_status ?? existingRow.schedule_status ?? null,
            schedule: mappedSchedule !== undefined ? mappedSchedule : (existingRow.schedule ?? null),
            scheduleId: incomingRow.schedule_id ?? incomingRow.schedule?.schedule_id ?? incomingRow.schedule?.id ?? existingRow.scheduleId ?? null,
            host: incomingRow.host || incomingRow.ip || incomingRow.ip_address || incomingRow.ipAddress || existingRow.host,
            ip_range: incomingRow.ip_range ?? incomingRow.ipRange ?? existingRow.ip_range,
            cred_ids: incomingRow.cred_ids ?? existingRow.cred_ids,
            agent_id: incomingRow.agent_id ?? incomingRow.agentId ?? existingRow.agent_id,
            scanTime: existingRow.scanTime || mappedRow.scanTime,
            reScan: isRescanSpecified ? isRescanEnabled : (Number(incomingRow.status) === 3 ? true : existingRow.reScan),
          };
        } else {
          // Prepend new row if it doesn't exist
          if (!incomingRow.isStatusUpdateOnly) {
            updatedData.unshift({
              ...mappedRow,
              scanTime: new Date().toISOString(),
              agent: mappedRow.agent || "N/A",
              user: mappedRow.user || "N/A",
            } as ScanRow);
          }
        }
      });

      // Maintain descending order sorted by scanTime
      return updatedData.sort((a, b) => {
        const aTime = parseFlexibleDate(a.scanTime);
        const bTime = parseFlexibleDate(b.scanTime);

        if (Number.isNaN(aTime) && Number.isNaN(bTime)) return 0;
        if (Number.isNaN(aTime)) return 1;
        if (Number.isNaN(bTime)) return -1;

        return bTime - aTime;
      });
    });

    setSchedulesData((prevData: ScheduleRow[]) => {
      const updatedData = [...prevData];

      myscanSocketData.forEach((incomingRow: any) => {
        const incomingScheduleObj = incomingRow.schedule || incomingRow.schedule_obj;
        // Only process rows that have schedule info
        const hasScheduleInfo = incomingRow.schedule_id || incomingScheduleObj?.schedule_id || incomingRow.schedule_status || (incomingScheduleObj && incomingScheduleObj.status);
        if (!hasScheduleInfo) return;

        const rawType = incomingRow.type ?? incomingRow.scan_type ?? "";

        const mappedScheduleRow: Partial<ScheduleRow> = {
          id: incomingRow.schedule_id ?? incomingRow.id ?? incomingRow.schedule_scan_id,
          scanName: incomingRow.scan_name ?? incomingRow.scanname ?? incomingRow.name ?? "",
          type: rawType,
          frequency:
            incomingScheduleObj?.frequency_name ??
            incomingScheduleObj?.frequency ??
            incomingRow.frequency_name ??
            incomingRow.frequency ??
            "One time",
          createdBy:
            incomingRow.created_by ??
            incomingRow.user ??
            incomingRow.user_name ??
            incomingRow.user_id ??
            "N/A",
          scheduledTime:
            incomingScheduleObj?.values?.time ??
            incomingRow.time ??
            incomingRow.scheduled_time ??
            incomingRow.scheduledTime ??
            "",
          scheduledStatus: (() => {
            const s = String(incomingRow.schedule_status ?? incomingScheduleObj?.status ?? incomingRow.status ?? "").trim().toLowerCase();
            if (s === "paused" || s === "pause") return "Paused";
            if (s === "active") return "Active";
            if (s === "completed") return "Completed";
            if (s === "cancelled") return "Cancelled";
            if (s === "inactive") return "Inactive";
            if (s === "scheduled") return "Scheduled";
            return s ? s.charAt(0).toUpperCase() + s.slice(1) : "Active";
          })(),
          numberOfAssets: Number(
            incomingRow.numberofdevices ??
            incomingRow.discovered_assets ??
            incomingRow.number_of_assets ??
            0
          ),
          agent: incomingRow.agent_name ?? incomingRow.agent ?? "N/A",
          date: incomingScheduleObj?.values?.date ?? incomingRow.date ?? incomingRow.schedule_date ?? "",
          scan_uuid: incomingRow.scan_uuid ?? incomingRow.schedule_id ?? incomingRow.id,
          date_added: incomingRow.date_time ?? incomingRow.date_added ?? "",
          reason: incomingRow.reason ?? "",
        };

        const incomingTargetId =
          incomingRow.schedule_id ??
          incomingRow.scan_uuid ??
          incomingRow.scan_id ??
          incomingRow.id ??
          incomingRow.onprem_posture_id ??
          incomingRow.onprem_id ??
          incomingRow.file_id;

        const existingIndex = updatedData.findIndex((row: ScheduleRow) => {
          if (
            incomingTargetId &&
            (row.scan_uuid === incomingTargetId ||
              row.id === incomingTargetId ||
              (row as any).schedule_id === incomingTargetId)
          ) {
            return true;
          }
          return false;
        });

        let finalIndex = existingIndex;
        if (finalIndex === -1) {
          const incomingName = incomingRow.scan_name || incomingRow.scanname || incomingRow.name;
          if (incomingName) {
            const candidates = updatedData
              .map((row, idx) => ({ row, idx }))
              .filter(({ row }) => {
                const nameMatches = row.scanName === incomingName || (row as any).name === incomingName;
                const stillInProgress =
                  String(row.scheduledStatus ?? "").toLowerCase() === "active" ||
                  String(row.scheduledStatus ?? "").toLowerCase() === "in progress" ||
                  String(row.scheduledStatus ?? "").toLowerCase() === "scheduled";
                return nameMatches && stillInProgress;
              });
            if (candidates.length === 1) {
              finalIndex = candidates[0].idx;
            }
          }
        }

        const cleanMappedRow = Object.fromEntries(
          Object.entries(mappedScheduleRow).filter(([, v]) => v !== undefined && v !== "")
        ) as Partial<ScheduleRow>;

        if (finalIndex !== -1) {
          updatedData[finalIndex] = {
            ...updatedData[finalIndex],
            ...cleanMappedRow,
          };
        } else {
          if (!incomingRow.isStatusUpdateOnly) {
            updatedData.unshift({
              ...mappedScheduleRow,
              scanName: mappedScheduleRow.scanName || "N/A",
              frequency: mappedScheduleRow.frequency || "One time",
              scheduledTime: mappedScheduleRow.scheduledTime || "",
              scheduledStatus: mappedScheduleRow.scheduledStatus || "Active",
            } as ScheduleRow);
          }
        }
      });

      return updatedData.sort((a, b) => {
        const aTime = parseFlexibleDate(a.date_added);
        const bTime = parseFlexibleDate(b.date_added);

        if (Number.isNaN(aTime) && Number.isNaN(bTime)) return 0;
        if (Number.isNaN(aTime)) return 1;
        if (Number.isNaN(bTime)) return -1;

        return bTime - aTime;
      });
    });

    // Check if any socket update transitioned to completed/success status === 3
    if (myscanSocketData.some((item: any) => Number(item.status) === 3)) {
      updateAssetCount();
      fetchScansData(false); // Silent background fetch without triggering screen/table loader
    }
  }, [myscanSocketData]);

  // Fetch scans data from API
  const fetchScansData = async (showLoader = true) => {
    if (showLoader) {
      setIsLoadingScans(true);
    }
    try {
      const payload: any = {
        timezone: "Asia/Calcutta",
        filter_sched: [],
        search: ""
      };

      if (location.state?.scan_id) {
        payload.scan_id = location.state.scan_id;
      }

      const response = await axiosInstance.post(END_POINTS.MY_SCAN_V2, payload);
      const data = response?.data?.data || response?.data || [];

      // Transform API response to match ScanRow interface
      const transformedData: ScanRow[] = Array.isArray(data)
        ? data.map((scan: any) => {
          const rawType = scan.type || scan.scan_type || "";
          const isDiscoveryType = String(rawType).toLowerCase().includes("discovery");
          const rawRescan = scan.reScan ?? scan.rescan;
          const isRescanEnabled =
            rawRescan === true ||
            rawRescan === 1 ||
            rawRescan === "1" ||
            String(rawRescan ?? "").toLowerCase() === "true";

          return {
            id: scan.id || scan.scan_uuid,
            name: scan.scan_name || scan.scanname || scan.name,
            scanName: scan.scan_name || scan.scanname,
            scan_uuid: scan.scan_uuid,
            scanId: scan.scan_id || scan.id,
            scheduleId: scan.schedule_id,
            schedule_id: scan.schedule_id,
            schedule_status: scan.schedule_status ?? scan.schedule?.status,
            statusTemp: scan.status,
            reScan: isRescanEnabled,
            schedule: scan.schedule,
            type: rawType,
            scanTime: scan.date_time || scan.scan_time || scan.scanTime || new Date().toISOString(),
            status: mapScanStatus(scan.status, rawType),
            assetsDiscovered: rawType === "onprem_posture" 
              ? (scan.discovered_assets ?? scan.assetsDiscovered ?? 0)
              : rawType === "aws_discovery_posture"
              ? (scan.discovered_assets ?? scan.assetsDiscovered ?? null)
              : (scan.discovered_assets ?? scan.assetsDiscovered ?? scan.numberofdevices ?? null),
            assetsAssessed: isDiscoveryType
              ? (scan.assessed_assets ?? scan.assessedAssets ?? scan.passed_count ?? 0)
              : (scan.passed_count ?? 0),
            passedAssets: scan.passed_count,
            TotalAssets: scan.total_count,
            misconfigurations: scan.misconfigurations ?? scan.vulnerabilities ?? null,
            agent: scan.agent_name || scan.agent || "N/A",
            agent_id: scan.agent_id || scan.agentId,
            user: scan.user || scan.created_by || scan.user_id || "N/A",
            onprem_posture_id: scan.onprem_posture_id,
            host: scan.host || scan.ip || scan.ip_address || scan.ipAddress,
            ip_range: scan.ip_range,
            cred_ids: scan.cred_ids || (scan.cred_id ? [scan.cred_id] : undefined),
          };
        })
          .sort((a, b) => {
            const aTime = parseFlexibleDate(a.scanTime);
            const bTime = parseFlexibleDate(b.scanTime);

            // Keep invalid or missing dates at the end.
            if (Number.isNaN(aTime) && Number.isNaN(bTime)) return 0;
            if (Number.isNaN(aTime)) return 1;
            if (Number.isNaN(bTime)) return -1;

            return bTime - aTime;
          })
        : [];

      setScansData(transformedData);

      // Call device count API if any scan has status === 3
      if (Array.isArray(data) && data.some((scan: any) => Number(scan.status) === 3)) {
        updateAssetCount();
      }
    } catch (error: any) {
      console.error("Error fetching scans:", error);
      toast.error(error?.response?.data?.message || "Failed to fetch scans");
      setScansData([]);
    } finally {
      setIsLoadingScans(false);
    }
  };

  const mapScheduleRows = (data: any): ScheduleRow[] => {
    const list = Array.isArray(data)
      ? data
      : Array.isArray(data?.summary_data)
        ? data.summary_data
        : Array.isArray(data?.list)
          ? data.list
          : Array.isArray(data?.data)
            ? data.data
            : [];

    const mappedList = list.map((schedule: any, index: number) => {
      const rawType = schedule.type ?? schedule.scan_type ?? "";

      return {
        id: schedule.id ?? schedule.schedule_id ?? schedule.schedule_scan_id ?? index + 1,
        scanName: schedule.scan_name ?? schedule.schedule_name ?? schedule.name ?? "",
        type: rawType,
        frequency:
          schedule.frequency ??
          schedule.frequency_name ??
          schedule.frequecy_name ??
          schedule.frequency_label ??
          "One time",
        createdBy:
          schedule.created_by ??
          schedule.user_name ??
          schedule.user ??
          schedule.createdBy ??
          "N/A",
        scheduledTime:
          schedule.time ??
          schedule.scheduled_time ??
          schedule.scheduledTime ??
          "",
        scheduledStatus: (() => {
          const s = String(schedule.schedule_status ?? schedule.status ?? "").trim().toLowerCase();
          if (s === "paused" || s === "pause") return "Paused";
          if (s === "active") return "Active";
          if (s === "completed") return "Completed";
          if (s === "cancelled") return "Cancelled";
          if (s === "inactive") return "Inactive";
          // Capitalize first letter as a fallback for any other status
          return s ? s.charAt(0).toUpperCase() + s.slice(1) : "Active";
        })(),
        numberOfAssets: Number(
          schedule.number_of_assets ??
          schedule.num_of_assets ??
          schedule.assets_count ??
          schedule.assetsDiscovered ??
          0
        ),
        agent: schedule.agent_name ?? schedule.agent ?? "N/A",
        date: schedule.date ?? schedule.schedule_date ?? "",
        scan_uuid: schedule.scan_uuid ?? schedule.schedule_id ?? schedule.id,
        date_added: schedule.date_added ?? "",
        reason: schedule.reason ?? "",
      };
    });

    return mappedList.sort((a, b) => {
      const aTime = a.date_added ? new Date(a.date_added).getTime() : 0;
      const bTime = b.date_added ? new Date(b.date_added).getTime() : 0;

      // Keep invalid or missing dates at the end.
      if (Number.isNaN(aTime) && Number.isNaN(bTime)) return 0;
      if (Number.isNaN(aTime)) return 1;
      if (Number.isNaN(bTime)) return -1;

      return bTime - aTime;
    });
  };

  const fetchSchedulesData = async () => {
    setIsLoadingSchedules(true);
    try {
      const timezone = "Asia/Calcutta";
      const response = await axiosInstance.get(`${END_POINTS.MY_SCAN_SCHEDULES}?timezone=${timezone}`);
      const data = response?.data?.data || response?.data || [];
      setSchedulesData(mapScheduleRows(data));
    } catch (error: any) {
      console.error("Error fetching schedules:", error);
      toast.error(error?.response?.data?.message || "Failed to fetch schedules");
      setSchedulesData([]);
    } finally {
      setIsLoadingSchedules(false);
    }
  };

  // Fetch scans on component mount
  useEffect(() => {
    if (!isChecked) {
      fetchScansData();
    } else {
      fetchSchedulesData();
    }
  }, [isChecked, location.state?.scan_id]);

  const getScanUuid = (row: ScanRow): string => {
    return String(row.scan_uuid ?? row.scanId ?? row.id);
  };

  const getScheduleUuid = (row: ScanRow): string => {
    return String(
      row.scheduleId ??
      row.schedule_id ??
      row.schedule?.schedule_id ??
      row.schedule?.id ??
      ""
    );
  };

  const getScanName = (row: ScanRow): string => {
    return row.scanName ?? row.name ?? "";
  };

  const isDiscoveryType = (scanType: unknown): boolean => {
    return String(scanType ?? "").toLowerCase().includes("discovery");
  };

  const isDiscoverySecurityType = (scanType: unknown): boolean => {
    const t = String(scanType ?? "").toLowerCase();
    return t.includes("discovery") && t.includes("posture");
  };

  // Handle New Scan type selection
  const handleNewScanTypeSelect = (scanType: ScanTypeId) => {
    setSelectedScanType(scanType);
    setIsNewScanDrawerOpen(true);
  };

  // Handle New Scan Drawer close
  const handleNewScanDrawerClose = () => {
    setIsNewScanDrawerOpen(false);
    // Refresh scans data after creating a new scan
    fetchScansData();
    if (isChecked) {
      fetchSchedulesData();
    }
  };

  const closeMyScanModals = () => {
    dispatch(
      myScanActions.renamePanel({
        isRenamePanelOpen: false,
        scan_id: "",
        scanName: "",
      })
    );
    dispatch(
      myScanActions.changeSchedule({
        isSchedulePanelOpen: false,
        scan_id: "",
        scanName: "",
        frequency: "",
        frequency_id: "",
        atTime: "",
        schedule_id: "",
        date: "",
        day: "",
        type: "",
      })
    );
  };

  const handleRename = (row: ScanRow) => {
    dispatch(
      myScanActions.renamePanel({
        isRenamePanelOpen: true,
        scan_id: getScanUuid(row),
        scanName: getScanName(row),
      })
    );
  };

  const handleSchedule = (row: ScanRow) => {
    const isSchedulable = String(row.schedule_status ?? "").toLowerCase() === "schedulable";
    const scheduleUuid = getScheduleUuid(row);
    dispatch(
      myScanActions.changeSchedule({
        isSchedulePanelOpen: true,
        scan_id: getScanUuid(row),
        scanName: getScanName(row),
        schedule_id: isSchedulable ? "" : scheduleUuid,
        frequency_id: row.schedule?.frequency_id ?? "",
        atTime: row.schedule?.values?.time ?? "",
        date: row.schedule?.values?.date ?? "",
        day: row.schedule?.values?.day ?? "",
        type: row.type || row.scan_type || "",
        ip_range: row.ip_range,
      })
    );
  };

  const handleScanInfo = (row: ScanRow) => {
    const scanUuid = getScanUuid(row);
    const rawType = String(row.type ?? "");
    const isPureDiscovery = isDiscoveryType(rawType) && !isDiscoverySecurityType(rawType);
    const isDiscoveryPosture = isDiscoverySecurityType(rawType);

    // Trigger scan-status API for Discovery and Discovery+Posture
    if (isPureDiscovery || isDiscoveryPosture) {
      try {
        const payloadUuid = isDiscoveryPosture && row.onprem_posture_id ? row.onprem_posture_id : scanUuid;
        axiosInstance.post("securityscan/scan-status", { scan_uuid: payloadUuid });
      } catch (error) {
        console.error("Error fetching scan status:", error);
      }
    }

    // Handle Pure Discovery type
    if (isPureDiscovery || rawType === "aws_discovery_posture") {
      const scanId = String(row.scanId ?? row.scan_uuid ?? row.id);
      navigate(`/discovery?scan_id=${encodeURIComponent(scanId)}`, {
        state: {
          fromScansV2: true,
          scanStatus: row.status,
          assetsAssessed: row.assetsAssessed,
          assetsDiscovered: row.assetsDiscovered,
        }
      });
      return;
    }



    dispatch(
      setEdit({
        edit: true,
        scan_uuid: scanUuid,
        status: String(row.statusTemp ?? row.status ?? ""),
        scanTime: row.scanTime ?? "",
        scanType: rawType,
        onprem_posture_id: row.onprem_posture_id,
      })
    );

    if (hasPermissions(ROLES_CONTENT_PROPS.SCAN_DETAILS)) {
      navigate(`/on-premises/scan-details/${scanUuid}`);
      return;
    }
    toast.error("You do not have permission to view scan details");
  };

  const handleEditSchedule = (row: ScheduleRow) => {
    const scheduleUuid = String(((row as any).schedule_id ?? row.scan_uuid) || row.id);
    dispatch(
      myScanActions.changeSchedule({
        isSchedulePanelOpen: true,
        scan_id: scheduleUuid,
        scanName: row.scanName,
        schedule_id: scheduleUuid,
        type: row.type,
      })
    );
  };

  const getRescanScanType = (row: ScanRow | null): ScanTypeId => {
    const rawType = String(row?.type ?? row?.scan_type ?? "").toLowerCase();

    if (rawType.includes("discovery") && rawType.includes("posture")) {
      return "onprem-discovery-posture";
    }
    if (rawType.includes("discovery")) {
      return "onprem-discovery";
    }
    if (rawType.includes("posture")) {
      return "onprem-posture";
    }

    return "onprem-discovery";
  };

  const handleRescan = (row: ScanRow) => {
    setRescanRowData(row);
    setIsRescanPopupOpen(true);
  };

  const handleCancel = async (row: ScanRow) => {
    try {
      const payload = {
        scanid: getScanUuid(row),
        scanIdNo: getScanName(row),
        cancel_rescan: "cancel",
      };

      const response = await axiosInstance.post("securityscan/cancel-rescan", payload);

      if (response.status === 200 || response.status === 201) {
        toast.success(response.data?.message || "Scan cancelled successfully");
        fetchScansData();
      }
    } catch (error: any) {
      console.error("Error cancelling scan:", error);
      toast.error(error?.response?.data?.message || "Failed to cancel scan");
    }
  };

  const handleCancelSchedule = async (row: ScheduleRow) => {
    try {
      const payload = {
        schedule_id: String(row.scan_uuid || row.id),
      };

      const response = await axiosInstance.post(END_POINTS.CANCEL_SCHEDULE, payload);

      if (response.status === 200 || response.status === 201) {
        toast.success(response.data?.message || "Schedule cancelled successfully");
        fetchSchedulesData();
      }
    } catch (error: any) {
      console.error("Error cancelling schedule:", error);
      toast.error(error?.response?.data?.message || "Failed to cancel schedule");
    }
  };

  const handlePauseSchedule = (row: ScheduleRow) => {
    setSelectedSchedule(row);
    setPauseResumeType("pause");
    setIsPauseResumeDialogOpen(true);
  };

  const handleResumeSchedule = (row: ScheduleRow) => {
    setSelectedSchedule(row);
    setPauseResumeType("resume");
    setIsPauseResumeDialogOpen(true);
  };

  const handleConfirmPauseResume = async () => {
    if (!selectedSchedule) return;

    try {
      const payload = {
        schedule_id: String(selectedSchedule.scan_uuid || selectedSchedule.id),
        status: pauseResumeType === "resume",
      };

      const response = await axiosInstance.put(END_POINTS.DISCOVERY_EDIT_SCHEDULE, payload);

      if (response.status === 200 || response.status === 201) {
        toast.success(`Schedule ${pauseResumeType === "pause" ? "paused" : "resumed"} successfully`);
        setIsPauseResumeDialogOpen(false);
        fetchSchedulesData();
      }
    } catch (error: any) {
      console.error(`Error ${pauseResumeType} schedule:`, error);
      toast.error(error?.response?.data?.message || `Failed to ${pauseResumeType} schedule`);
    }
  };

  const canManageSchedules =
    hasPermissions(ROLES_CONTENT_PROPS.EDIT_SCHEDULE) ||
    hasPermissions(ROLES_CONTENT_PROPS.SCHEDULE);

  const getScheduleLabel = (row: ScanRow): string => {
    return row.schedule_status === "scheduled" ? "Change Schedule" : "Schedule Scan";
  };

  const shouldShowSchedule = (row: ScanRow): boolean => {
    if (!canManageSchedules) return false;

    // Hide schedule option when scan is in progress
    const status = String(row.status ?? "").toLowerCase();
    const isInProgress = status === "in progress" || status === "processing" ||
      row.statusTemp === 1 || row.statusTemp === 2 ||
      row.statusTemp === "1" || row.statusTemp === "2";
    if (isInProgress) return false;

    if (!row.schedule_status) return true;
    return row.schedule_status === "schedulable" || row.schedule_status === "scheduled";
  };

  const shouldShowCancel = (row: ScanRow): boolean => {
    return (
      row.statusTemp === 1 ||
      row.statusTemp === 2 ||
      row.statusTemp === "1" ||
      row.statusTemp === "2"
    );
  };

  const shouldShowRescan = (row: ScanRow): boolean => {
    // Basic check for cancellation
    if (row.status === "Cancelled") return false;

    const statusTemp = Number(row.statusTemp);
    const isFailed = statusTemp === 4;
    const isPosture = String(row.type ?? "").toLowerCase().includes("posture");
    const isRescanEnabled = Boolean(row.reScan);

    let agentStatus = String(row.agent_status ?? "").toLowerCase();
    if (row.agent_id && agentStatuses[row.agent_id]) {
      agentStatus = agentStatuses[row.agent_id];
    }

    // If status 4 (Failed), rescan is true, and it's onprem posture, hide Rescan in kebab
    if (isFailed && isPosture && isRescanEnabled) {
      return false;
    }

    return isRescanEnabled;
  };

  const scanColumnDefs = useScansColumnDefs({
    onRename: handleRename,
    onScanInfo: handleScanInfo,
    onSchedule: handleSchedule,
    onRescan: handleRescan,
    onCancel: handleCancel,
    getScheduleLabel,
    shouldShowSchedule,
    shouldShowCancel,
    shouldShowRescan,
  });

  const scheduleColumnDefs = useSchedulesColumnDefs({
    onEditSchedule: handleEditSchedule,
    onCancelSchedule: handleCancelSchedule,
    onPauseSchedule: handlePauseSchedule,
    onResumeSchedule: handleResumeSchedule,
  });

  // AG Grid inline styles
  const agGridInlineStyles = useMemo(
    () => ({
      padding: "0px",
      "& .ag-center-cols-clipper": {
        marginRight: 0,
      },
      "& .ag-center-cols-viewport": {
        marginRight: 0,
      },
      "& .ag-cell.ag-cell-status-badge": {
        overflow: "visible",
      },
      "& .ag-cell.ag-cell-status-badge .ag-cell-wrapper": {
        overflow: "visible",
      },
      "& .ag-cell.ag-cell-status-badge .ag-cell-value": {
        overflow: "visible",
        textOverflow: "clip",
        whiteSpace: "normal",
      },
    }),
    []
  );

  // Toggle handler
  const handleSwitchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    setIsChecked(checked);
    if (checked) {
      setSearchParams({ view: "schedule" });
    } else {
      setSearchParams({});
    }
  };

  // Add schedule handler – opens the New Scan drawer
  const handleAddSchedule = () => {
    setIsNewScanDrawerOpen(true);
  };

  // Row click handlers
  const handleScanRowClick = (rowData: any) => {
    const row = rowData as ScanRow;
    const status = String(row.status ?? "").toLowerCase();
    const isInProgress = status === "in progress" || status === "processing";
    const isFailed = row.statusTemp === 4 || row.statusTemp === "4" || status === "failed";
    const isCancelled = row.statusTemp === 5 || row.statusTemp === "5" || status === "cancelled";
    const rawType = String(row.type ?? "");
    const isAwsInProgress = (row.statusTemp === 1 || row.statusTemp === 2 || row.statusTemp === "1" || row.statusTemp === "2") && rawType.toLowerCase().startsWith("aws");
    const isAwsFailed = (row.statusTemp === 4 || row.statusTemp === "4" || status === "failed") && rawType.toLowerCase().startsWith("aws");

    if (isCancelled || isAwsInProgress || isAwsFailed) {
      return;
    }

    // Discovery + Security combined type → new toggle page
    if (isDiscoverySecurityType(rawType) && rawType !== "aws_discovery_posture") {
      const scanUuid = getScanUuid(row);
      dispatch(
        setEdit({
          edit: true,
          scan_uuid: scanUuid,
          status: String(row.statusTemp ?? row.status ?? ""),
          scanTime: row.scanTime ?? "",
          scanType: rawType,
          onprem_posture_id: row.onprem_posture_id,
        })
      );
      if (hasPermissions(ROLES_CONTENT_PROPS.SCAN_DETAILS)) {
        navigate(`/on-premises/discovery-security/${scanUuid}`, {
          state: {
            fromScansV2: true,
            scanStatus: row.status,
            assetsAssessed: row.assetsAssessed,
            assetsDiscovered: row.assetsDiscovered,
          }
        });
        return;
      }
      toast.error("You do not have permission to view scan details");
      return;
    }

    // Pure Discovery type
    if (isDiscoveryType(rawType) && rawType !== "aws_discovery_posture") {
      if (isInProgress) {
        return; // Disable row click for in-progress discovery
      }
      const scanId = String(row.scanId ?? row.scan_uuid ?? row.id);
      navigate(`/discovery?scan_id=${encodeURIComponent(scanId)}`, {
        state: {
          fromScansV2: true,
          scanStatus: row.status,
          assetsAssessed: row.assetsAssessed,
          assetsDiscovered: row.assetsDiscovered,
        }
      });
      return;
    }

    // Pure Security type
    const scanUuid = getScanUuid(row);
    if (hasPermissions(ROLES_CONTENT_PROPS.SCAN_DETAILS)) {
      dispatch(
        setEdit({
          edit: true,
          scan_uuid: scanUuid,
          status: String(row.statusTemp ?? row.status ?? ""),
          scanTime: row.scanTime ?? "",
          scanType: rawType,
          onprem_posture_id: row.onprem_posture_id,
        })
      );
      if (isInProgress || isFailed) {
        navigate(`/on-premises/scan-details/${scanUuid}`);
      } else {
        navigate(`/onprem/scan/${scanUuid}`);
      }
      return;
    }
    toast.error("You do not have permission to view scan details");
  };



  // ──────────────────────────────────────────
  // Header Actions (right side of search bar)
  // ──────────────────────────────────────────

  const headerActions = (
    <Box display="flex" alignItems="center" gap={2}>
      <Typography sx={{ marginRight: "3px", fontSize: "14px" }}>
        Scans
      </Typography>
      <FormControlLabel
        control={
          <IOSSwitch
            checked={isChecked}
            onChange={handleSwitchChange}
            width={40}
            height={20}
            thumbSize={16}
          />
        }
        label={
          <Typography variant="body2" sx={{ ml: 1, fontSize: "14px" }}>
            Schedules
          </Typography>
        }
        labelPlacement="end"
      />
      {isChecked && hasPermissions(ROLES_CONTENT_PROPS.NEW_SCAN) && (
        <IconButton
          aria-label="add schedule"
          color="primary"
          onClick={handleAddSchedule}
          sx={{ "&:focus": { outline: "none" } }}
        >
          <AddIcon />
        </IconButton>
      )}
    </Box>
  );

  // ──────────────────────────────────────────
  // Render
  // ──────────────────────────────────────────

  return (
    <Card
      variant="outlined"
      sx={{ height: "calc(100vh - 120px)", borderRadius: 3 }}
    >
      {isChecked ? (
        <AgGridTable
          columnDefs={scheduleColumnDefs}
          styles={agGridInlineStyles}
          data={schedulesData}
          height="calc(100vh - 160px)"
          showSearch
          headerActions={headerActions}
          isLoading={isLoadingSchedules}
        />
      ) : (
        <AgGridTable
          columnDefs={scanColumnDefs}
          styles={agGridInlineStyles}
          data={scansData}
          height="calc(100vh - 160px)"
          showSearch
          headerActions={headerActions}
          onRowClick={handleScanRowClick}
          isLoading={isLoadingScans}
          gridOptions={{
            getRowStyle: (params) => {
              const row = params.data as ScanRow;
              if (!row) return undefined;
              const status = String(row.status ?? "").toLowerCase();
              const isInProgress = status === "in progress" || status === "processing";
              const isCancelled = row.statusTemp === 5 || row.statusTemp === "5" || status === "cancelled";
              const rawType = String(row.type ?? "").toLowerCase();
              const isDiscovery = rawType.includes("discovery");
              const isDiscoverySecurity = isDiscovery && rawType.includes("posture");
              const isAwsInProgress = (row.statusTemp === 1 || row.statusTemp === 2 || row.statusTemp === "1" || row.statusTemp === "2") && rawType.toLowerCase().startsWith("aws");
              const isAwsFailed = (row.statusTemp === 4 || row.statusTemp === "4" || status === "failed") && rawType.toLowerCase().startsWith("aws");

              if (isCancelled || (isDiscovery && !isDiscoverySecurity && isInProgress) || isAwsInProgress || isAwsFailed) {
                return { cursor: "default" };
              }
              return { cursor: "pointer" };
            },
          }}
        />
      )}

      {/* New Scan Drawer */}
      <NewScanDrawer
        open={isNewScanDrawerOpen}
        initialScanType={selectedScanType}
        onClose={handleNewScanDrawerClose}
        agents={[]}
        credentials={[]}
        isFromScheduleView={isChecked}
      />

      {/* Create Schedule Drawer */}
      <CreateScheduleDrawer
        open={isCreateScheduleOpen}
        onClose={() => setIsCreateScheduleOpen(false)}
        onCreated={() => {
          console.log("Schedule created – refresh data");
          fetchSchedulesData();
        }}
      />

      <Dialog
        open={isPauseResumeDialogOpen}
        onClose={() => setIsPauseResumeDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: "10px",
            padding: "16px",
            minWidth: "280px",
            maxWidth: "320px",
            textAlign: "center",
          },
        }}
      >
        <DialogContent sx={{ p: 0 }}>
          <Stack spacing={2} alignItems="center">
            <Typography variant="body1" sx={{ fontWeight: 600, fontSize: "1rem", lineHeight: 1.4 }}>
              Are you sure want to {pauseResumeType === "pause" ? "Pause" : "Resume"} “
              {selectedSchedule?.scanName}”?
            </Typography>

            <Stack direction="row" spacing={1.5} sx={{ width: "100%" }}>
              <Button
                variant="outlined"
                fullWidth
                size="small"
                onClick={() => setIsPauseResumeDialogOpen(false)}
                sx={{
                  borderRadius: "6px",
                  borderColor: "#ddd",
                  color: "#666",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "13px",
                  py: 0.6,
                  "&:hover": { borderColor: "#bbb" },
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                fullWidth
                size="small"
                onClick={handleConfirmPauseResume}
                sx={{
                  borderRadius: "6px",
                  backgroundColor: "#3A2ADA",
                  color: "#fff",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "13px",
                  py: 0.6,
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: "#2E1FBF",
                  },
                }}
              >
                {pauseResumeType === "pause" ? "Pause" : "Resume"}
              </Button>
            </Stack>
          </Stack>
        </DialogContent>
      </Dialog>

      {renameDetails.isRenamePanelOpen && (
        <RenameModel
          open={Boolean(renameDetails.isRenamePanelOpen)}
          onClose={closeMyScanModals}
          scanId={renameDetails.scan_id}
          scanName={renameDetails.scanName}
          refetch={() => {
            fetchScansData();
            fetchSchedulesData();
          }}
        />
      )}

      {renameDetails.isSchedulePanelOpen && (
        <ChangeScheduleModal
          open={Boolean(renameDetails.isSchedulePanelOpen)}
          onClose={closeMyScanModals}
          scheduleId={renameDetails.schedule_id}
          scheduleName={renameDetails.scanName}
          isScansV2ScheduleFlow={true}
          initialValues={{
            scan_name: renameDetails.scanName,
            frequency_id: renameDetails.frequency_id,
            time: renameDetails.atTime,
            date: renameDetails.date,
            day: renameDetails.day,
            schedule_id: renameDetails.schedule_id,
            scan_id: renameDetails.scan_id,
            ip_range: renameDetails.ip_range,
          }}
          type={renameDetails.type}
          refetch={() => {
            fetchScansData();
            fetchSchedulesData();
          }}
        />
      )}

      <DiscoveryRescanPopup
        open={isRescanPopupOpen}
        onClose={() => setIsRescanPopupOpen(false)}
        rowData={rescanRowData}
        defaultScanType={rescanRowData ? getRescanScanType(rescanRowData) : undefined}
        disableScanType={Boolean(rescanRowData)}
        hideCreateCredential={true}
        initialAgents={cloudAgents}
        title="Rescan"
        onRescanSuccess={() => {
          fetchScansData();
        }}
      />
    </Card>
  );
};

export default ScansV2Page;
